import { useState } from "react";
import { Stack, TextField, MenuItem, Button, Grid, Typography } from "@mui/material";
import { Risk } from "../helpers/InterfacesServer";
import { useAppSelector } from "../store/store";
import { useLocation } from "react-router-dom";
import { showConfirmBox, getRiskStatusString, getFilteredAndSortedRisks, canSendRiskForEntityHeadReview, canSubmitRisk } from "../helpers/Utils";
import { RiskIdentificationDisplay, RiskGroupFilters, RiskStatus, FormEditMode, RiskSortType, DisplayRefreshTrigger } from "../helpers/Constants";
import { deleteRisk, entityHeadReviewRisk, sendMultipleRisksForEntityHeadReview, sendRiskForEntityHeadReview, submitMultipleRisks } from "../api-services/RiskApi";
import { HorizontalSplitPane } from "../layouts/HorizontalSplitPane";
import { RiskIdentificationWidget } from "../components/risk-identification/RiskIdentificationWidget";
import { RiskIdentificationDetails } from "../components/risk-identification/RiskIdentificationDetails";
import { useDisplay, useRiskAssessment, useRisks, useSelectedRisk } from "../helpers/GlobalHooks";
import { ThumbUp, ThumbDown } from "@mui/icons-material";
import CustomDialog from "../layouts/CustomDialog";
import { enqueueSnackbar } from "notistack";



export function RiskIdentificationEditor() {
    // state independent variables
    const loggedOnUserProfile = useAppSelector(state => state.loggedOnUserProfile.value);
    const riskRelatedTables = useAppSelector(state => state.riskRelatedTables.value);
    const location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    const assessmentIdForRiskIdentification: number | null = queryParams.get("assessmentIdForRiskIdentification") == null ? null : Number(queryParams.get("assessmentIdForRiskIdentification"))
    const defaultRiskId: number | null = queryParams.get("defaultRiskId") == null ? null : Number(queryParams.get("defaultRiskId"))
    const defaultRiskGroupFilter: number | null = queryParams.get("defaultRiskGroupFilter") == null ? null : Number(queryParams.get("defaultRiskGroupFilter"))
    const defaultDisplay: number = queryParams.get("defaultDisplay") != null ? Number(queryParams.get("defaultDisplay")) : (assessmentIdForRiskIdentification != null ? RiskIdentificationDisplay.CREATE_RISK : RiskIdentificationDisplay.PREVIEW_RISK)
    // states and state dependent variables
    const [risks, setRisks, refreshRisks] = useRisks()
    const [riskAssessmentForRiskIdentification,] = useRiskAssessment(assessmentIdForRiskIdentification)
    const [riskSortType, setRiskSortType] = useState<RiskSortType>(RiskSortType.SortDateCreatedDesc)
    const [categoryFilter, setCategoryFilter] = useState('All')
    const [entityFilter, setEntityFilter] = useState('All')
    const [riskAssessmentFilter, setRiskAssessmentFilter] = useState(assessmentIdForRiskIdentification != null ? assessmentIdForRiskIdentification + '' : 'All')
    const [statusFilter, setStatusFilter] = useState('All')
    const [riskGroupFilter, setRiskGroupFilter] = useState<number>(defaultRiskGroupFilter ?? RiskGroupFilters.MY_RISKS)
    const [displayRefreshTrigger, setDisplayRefreshTrigger] = useState<DisplayRefreshTrigger | null>(null)
    const [displayRiskAssessmentWelcomeMessage, setDisplayRiskAssessmentWelcomeMessage] = useState(true)
    const filteredRisks = getFilteredAndSortedRisks(loggedOnUserProfile, risks, entityFilter, categoryFilter, statusFilter, riskAssessmentFilter, "All", riskGroupFilter, riskSortType)
    const { selectedRisk, setSelectedRisk } = useSelectedRisk(filteredRisks, defaultRiskId, displayRefreshTrigger !== DisplayRefreshTrigger.ItemListFiltered, displayRefreshTrigger === DisplayRefreshTrigger.ItemSelected)
    const { display, setDisplay } = useDisplay(filteredRisks, defaultDisplay, displayRefreshTrigger !== DisplayRefreshTrigger.ItemListFiltered, displayRefreshTrigger === DisplayRefreshTrigger.ItemSelected, RiskIdentificationDisplay.CREATE_RISK, RiskIdentificationDisplay.PREVIEW_RISK)

    const [openEntityHeadReviewDialog, setOpenEntityHeadReviewDialog] = useState<boolean>(false)
    const [entityHeadRejectionComments, setEntityHeadRejectionComments] = useState<string>("")


    function handleConfirmSubmitAllRisks() {
        showConfirmBox("You will not be able to make changes after submitting. Are you sure you want to Submit all Risks?", "Confirm Submit")
            .then((result) => {
                if (result.isConfirmed){
                    let risksToSubmit = risks.filter(risk => canSubmitRisk(risk, loggedOnUserProfile))
                    if (risksToSubmit.length === 0) {
                        enqueueSnackbar("No Risks available/ready for submission", { variant: 'info' });
                        return;
                    }
                    submitMultipleRisks(risksToSubmit).then(risksResult => {
                        for (let riskResult of risksResult)
                            setRisks(risks => risks.map(r => r.id !== riskResult.id ? r : riskResult))
                        if (risksResult.length > 0) setSelectedRisk(risksResult[0])
                    })
            }
            })
    }

    function handleConfirmDeleteRisk(riskIdToDelete: number) {
        showConfirmBox("Are you sure you want to delete this Risk?", "Confirm Delete")
            .then((result) => {
                if (result.isConfirmed)
                    deleteRisk(riskIdToDelete).then(() => {
                        setRisks(risks => risks.filter(inst => inst.id !== riskIdToDelete))
                        setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered) //needed to refresh and select first risk in list
                    })
            })
    }

    function handleConfirmSendRiskForEntityReview(risk: Risk) {
        showConfirmBox(`Are you sure you want to send this Draft Risk: ${risk.name} to the Entity Head for review?`, "Confirm Send for Entity Head Review")
            .then((result) => {
                if (result.isConfirmed)
                    sendRiskForEntityHeadReview(risk).then(riskResult => {
                        handleRiskChange(riskResult.id, riskResult, FormEditMode.EDIT, RiskIdentificationDisplay.VIEW_RISK);
                    })
            })
    }

    function handleConfirmSendAllRisksForEntityReview() {
        showConfirmBox("Are you sure you want to send all Draft Risks to the Entity Head for review?", "Confirm Send for Entity Head Review")
            .then((result) => {
                if (result.isConfirmed) {
                    let risksToSend = risks.filter(risk => canSendRiskForEntityHeadReview(risk, loggedOnUserProfile))
                    if (risksToSend.length === 0) {
                        enqueueSnackbar("No Risks available/ready for Entity Head Review", { variant: 'info' });
                        return;
                    }
                    sendMultipleRisksForEntityHeadReview(risksToSend).then(risksResult => {
                        for (let riskResult of risksResult)
                            setRisks(risks => risks.map(r => r.id !== riskResult.id ? r : riskResult))
                        if (risksResult.length > 0) setSelectedRisk(risksResult[0])
                    })
                }
            })
    }

    function handleEntityHeadValidateRisk(risk: Risk) {
        setOpenEntityHeadReviewDialog(false);
        entityHeadReviewRisk({ ...risk, entityHeadRejectionComments: entityHeadRejectionComments ?? "Validated" }, true).then(result => {
            setEntityHeadRejectionComments("");
            handleRiskChange(result.id, result, FormEditMode.EDIT, RiskIdentificationDisplay.VIEW_RISK);
        })
    }

    function handleEntityHeadRejectRisk(risk: Risk) {
        setOpenEntityHeadReviewDialog(false);
        entityHeadReviewRisk({ ...risk, entityHeadRejectionComments: entityHeadRejectionComments }, false).then(result => {
            setEntityHeadRejectionComments("");
            handleRiskChange(result.id, result, FormEditMode.EDIT, RiskIdentificationDisplay.VIEW_RISK);
        })
    }

    function handleRiskChange(id: number, risk: Risk, formEditMode: FormEditMode, display: RiskIdentificationDisplay) {
        if (formEditMode === FormEditMode.EDIT) {
            setRisks(risks => risks.map(inst => inst.id === id ? risk : inst))
            setSelectedRisk(risk)
            setDisplay(display)
        }
        else if (formEditMode === FormEditMode.CREATE) {
            setRisks(risks => [risk, ...risks])
            setSelectedRisk(risk)
            setDisplay(display)
        }
    }

    function handleChangeRiskIdentificationDisplay(display: RiskIdentificationDisplay, risk?: Risk | null) {
        if (risk != null) setSelectedRisk(risk)
        setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemSelected)
        setDisplay(display);
    }


    return (
        loggedOnUserProfile &&
        <>
            {openEntityHeadReviewDialog && selectedRisk != null &&
                <CustomDialog dialogTitle='Entity Head Review' openDialog={openEntityHeadReviewDialog} setOpenDialog={setOpenEntityHeadReviewDialog}>
                    <>
                        <Grid item xs={12}>
                            <Typography color="textSecondary" ><span style={{ color: '#cc0000' }}>*</span> <span style={{ color: '#000000' }}>Comments for Risk: </span><i>{selectedRisk.name}</i></Typography>
                            <br />
                            <TextField fullWidth required name="description" type="text" variant="outlined" multiline size="small" minRows={3}
                                value={entityHeadRejectionComments ?? ''} onChange={(event) => setEntityHeadRejectionComments(event.target.value)} />
                        </Grid>
                        <br />
                        <Grid item xs={12} style={{ textAlign: 'right' }}>
                            <Button variant='contained' size='small' color='success' onClick={() => handleEntityHeadValidateRisk(selectedRisk)} > <ThumbUp /> &nbsp;&nbsp;Validate</Button>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            <Button variant='contained' size='small' color='secondary' onClick={() => handleEntityHeadRejectRisk(selectedRisk)} disabled={!entityHeadRejectionComments}> <ThumbDown /> &nbsp;&nbsp;Reject</Button>
                        </Grid>
                    </>
                </CustomDialog>
            }
            {assessmentIdForRiskIdentification == null &&
                <Stack direction="row" spacing={2} style={{ width: '100%', paddingLeft: 5, paddingRight: 5, paddingTop: 0, paddingBottom: 5 }} >
                    <TextField fullWidth select variant="standard" size="small" name="statusFilter" value={statusFilter} onChange={(e) => { setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered); setStatusFilter(e.target.value) }}>
                        <MenuItem key={0} value="All">All Status</MenuItem >
                        <MenuItem key={RiskStatus.DRAFT} value={RiskStatus.DRAFT}>{getRiskStatusString(RiskStatus.DRAFT)}</MenuItem >
                        <MenuItem key={RiskStatus.AWAITING_ENTITY_HEAD_REVIEW} value={RiskStatus.AWAITING_ENTITY_HEAD_REVIEW}>{getRiskStatusString(RiskStatus.AWAITING_ENTITY_HEAD_REVIEW)}</MenuItem >
                        <MenuItem key={RiskStatus.SUBMITTED} value={RiskStatus.SUBMITTED}>{getRiskStatusString(RiskStatus.SUBMITTED)}</MenuItem >
                        <MenuItem key={RiskStatus.APPROVED} value={RiskStatus.APPROVED}>{getRiskStatusString(RiskStatus.APPROVED)}</MenuItem >
                        <MenuItem key={RiskStatus.CHAMPION_NOMINATED} value={RiskStatus.CHAMPION_NOMINATED}>{getRiskStatusString(RiskStatus.CHAMPION_NOMINATED)}</MenuItem >
                        <MenuItem key={RiskStatus.CHAMPION_NOMINATION_REJECTED} value={RiskStatus.CHAMPION_NOMINATION_REJECTED}>{getRiskStatusString(RiskStatus.CHAMPION_NOMINATION_REJECTED)}</MenuItem >
                        <MenuItem key={RiskStatus.ASSIGNED_TO_CHAMPION} value={RiskStatus.ASSIGNED_TO_CHAMPION}>{getRiskStatusString(RiskStatus.ASSIGNED_TO_CHAMPION)}</MenuItem >
                        <MenuItem key={RiskStatus.ACTION_PLAN_SUBMITTED} value={RiskStatus.ACTION_PLAN_SUBMITTED}>{getRiskStatusString(RiskStatus.ACTION_PLAN_SUBMITTED)}</MenuItem >
                        <MenuItem key={RiskStatus.ACTION_PLAN_REJECTED} value={RiskStatus.ACTION_PLAN_REJECTED}>{getRiskStatusString(RiskStatus.ACTION_PLAN_REJECTED)}</MenuItem >
                        <MenuItem key={RiskStatus.ACTION_PLAN_APPROVED} value={RiskStatus.ACTION_PLAN_APPROVED}>{getRiskStatusString(RiskStatus.ACTION_PLAN_APPROVED)}</MenuItem >
                        <MenuItem key={RiskStatus.ACTION_PLAN_IMPLEMENTATION_SUBMITTED} value={RiskStatus.ACTION_PLAN_IMPLEMENTATION_SUBMITTED}>{getRiskStatusString(RiskStatus.ACTION_PLAN_IMPLEMENTATION_SUBMITTED)}</MenuItem >
                        <MenuItem key={RiskStatus.ACTION_PLAN_IMPLEMENTATION_REJECTED_BY_SPONSOR} value={RiskStatus.ACTION_PLAN_IMPLEMENTATION_REJECTED_BY_SPONSOR}>{getRiskStatusString(RiskStatus.ACTION_PLAN_IMPLEMENTATION_REJECTED_BY_SPONSOR)}</MenuItem >
                        <MenuItem key={RiskStatus.UNEXPOSED} value={RiskStatus.UNEXPOSED}>{getRiskStatusString(RiskStatus.UNEXPOSED)}</MenuItem >
                    </TextField>
                    <TextField fullWidth select variant="standard" size="small" name="riskAssessmentFilter" value={riskAssessmentFilter} onChange={(e) => { setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered); setRiskAssessmentFilter(e.target.value) }}>
                        <MenuItem value="All">All Risk Types</MenuItem >
                        <MenuItem value='0'>Unassessed Risk</MenuItem >
                        {[...new Map(risks.filter(row => row.sourceAssessment != null).map(item => [item.sourceAssessment!.id, item.sourceAssessment!])).values()].map(row => <MenuItem key={row!.id} value={row!.id}>{row!.assessmentName}</MenuItem >)}
                    </TextField>
                    <TextField fullWidth select variant="standard" size="small" name="entityFilter" value={entityFilter} onChange={(e) => { setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered); setEntityFilter(e.target.value) }}>
                        <MenuItem value="All">All Entities</MenuItem >
                        {riskRelatedTables.entities.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem >)}
                    </TextField>
                    <TextField fullWidth select variant="standard" size="small" name="categoryFilter" value={categoryFilter} onChange={(e) => { setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered); setCategoryFilter(e.target.value) }}>
                        <MenuItem value="All">All Categories</MenuItem >
                        {riskRelatedTables.categories.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem >)}
                    </TextField>
                </Stack>
            }

            <HorizontalSplitPane leftWidth={299} rightWidthDynamic
                left={
                    <RiskIdentificationWidget risks={filteredRisks} selectedRisk={selectedRisk} onRiskSortTypeChange={setRiskSortType}
                        assessmentIdForRiskIdentification={assessmentIdForRiskIdentification} riskAssessmentForRiskIdentification={riskAssessmentForRiskIdentification}
                        riskGroupFilter={riskGroupFilter} onRiskGroupFilterChange={(e) => { setDisplayRefreshTrigger(DisplayRefreshTrigger.ItemListFiltered); setRiskGroupFilter(Number(e)) }}
                        onChangeRiskIdentificationDisplay={handleChangeRiskIdentificationDisplay}
                        onClickDeleteRisk={handleConfirmDeleteRisk}
                        onClickSubmitAllRisks={handleConfirmSubmitAllRisks}
                        OnClickSendRiskForEntityReview={handleConfirmSendRiskForEntityReview}
                        onClickSendAllRisksForEntityReview={handleConfirmSendAllRisksForEntityReview}
                        onClickEntityHeadReviewRisk={() => setOpenEntityHeadReviewDialog(true)}
                        onClickRefreshRisks={() => refreshRisks()}
                    />}
                right={
                    <RiskIdentificationDetails risks={risks} selectedRisk={selectedRisk} onRiskChange={handleRiskChange}
                        assessmentIdForRiskIdentification={assessmentIdForRiskIdentification} riskAssessmentForRiskIdentification={riskAssessmentForRiskIdentification}
                        displayRiskAssessmentWelcomeMessage={displayRiskAssessmentWelcomeMessage} onClickBeginRiskAssessmentIdentification={() => setDisplayRiskAssessmentWelcomeMessage(false)}
                        display={display} onChangeRiskIdentificationDisplay={handleChangeRiskIdentificationDisplay}
                        onClickDeleteRisk={handleConfirmDeleteRisk}
                        OnClickSendRiskForEntityReview={handleConfirmSendRiskForEntityReview}
                        onClickEntityHeadReviewRisk={() => setOpenEntityHeadReviewDialog(true)}
                    />}
            />
        </>
    )
}